/*
  © 2024 KondaSoft
  https://www.kondasoft.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.form.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.form.querySelector('.btn-atc')
    this.buyBtn = this.form.querySelector('[data-buy-now-link]')
    this.stockIndicator = this.form.querySelector('.product-stock-indicator')
    this.isVariantForm = Boolean(this.form.dataset.variants)
    this.variants = this.isVariantForm ? JSON.parse(this.form.dataset.variants || '[]') : []
    this.currentVariant = this.isVariantForm
      ? this.variants.find(variant => String(variant.id) === this.form.querySelector('[name="id"]')?.value)
      : null
    this.atcDefaultText = this.atcBtn?.innerHTML || ''
    this.buyDefaultText = this.buyBtn?.innerHTML || ''
    this.atcInitiallyDisabled = this.atcBtn?.disabled || false

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))
    this.initAccessories()

    if (this.isVariantForm) {
      this.updateVariantOptionStates()
      this.adjustStock(this.currentVariant)
      this.adjustButtons(this.currentVariant)
    }
  }

  initAccessories () {
    this.accessoriesWrapper = this.form.querySelector('.product-block-accessories')
    if (!this.accessoriesWrapper) return

    this.accessoryCheckboxes = Array.from(this.accessoriesWrapper.querySelectorAll('.accessory-checkbox'))
    this.accessoriesSubtotal = this.accessoriesWrapper.querySelector('[data-accessories-subtotal]')
    this.accessoriesSubtotalValue = this.accessoriesWrapper.querySelector('[data-accessories-subtotal-value]')

    this.accessoryCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => this.updateAccessoriesState())
    })

    this.accessoryItems = Array.from(this.accessoriesWrapper.querySelectorAll('.accessory-item'))
    this.accessoryItems.forEach((item) => {
      item.addEventListener('click', (event) => this.onAccessoryItemClick(event, item))
    })

    this.updateAccessoriesState()
  }

  onAccessoryItemClick (event, item) {
    if (event.target.closest('.accessory-checkbox')) return
    if (!window.matchMedia('(max-width: 599.98px)').matches) return

    event.preventDefault()

    const checkbox = item.querySelector('.accessory-checkbox')
    if (!checkbox || checkbox.disabled) return

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event('change', { bubbles: true }))
  }

  updateAccessoriesState () {
    if (!this.accessoriesWrapper) return

    const selectedCheckboxes = this.getSelectedAccessories()
    const subtotal = selectedCheckboxes.reduce((total, checkbox) => {
      return total + Number(checkbox.dataset.price || 0)
    }, 0)

    this.accessoryCheckboxes.forEach((checkbox) => {
      const item = checkbox.closest('.accessory-item')
      if (!item) return

      const isSelected = checkbox.checked && !checkbox.disabled
      item.classList.toggle('is-selected', isSelected)
      item.setAttribute('aria-selected', isSelected ? 'true' : 'false')
    })

    if (this.accessoriesSubtotal && this.accessoriesSubtotalValue) {
      this.accessoriesSubtotal.classList.add('is-updating')
      this.accessoriesSubtotal.hidden = selectedCheckboxes.length === 0
      this.accessoriesSubtotalValue.textContent = window.Shopify.formatMoney(subtotal)
      window.setTimeout(() => {
        this.accessoriesSubtotal?.classList.remove('is-updating')
      }, 120)
    }
  }

  getSelectedAccessories () {
    if (!this.accessoryCheckboxes) return []
    return this.accessoryCheckboxes.filter((checkbox) => checkbox.checked && !checkbox.disabled)
  }

  setAccessoriesDisabled (disabled) {
    if (!this.accessoryCheckboxes) return

    this.accessoryCheckboxes.forEach((checkbox) => {
      if (checkbox.closest('.accessory-item')?.classList.contains('is-unavailable')) return
      checkbox.disabled = disabled
      checkbox.setAttribute('aria-disabled', disabled ? 'true' : 'false')
    })

    this.accessoriesWrapper?.classList.toggle('is-submitting', disabled)
  }

  getSelectedOptions () {
    const selectedOptions = []
    this.productOptions.forEach(elem => {
      if (elem.type === 'radio' && elem.checked) {
        selectedOptions.push(elem.value)
      } else if (elem.type === 'select-one') {
        selectedOptions.push(elem.value)
      }
    })
    return selectedOptions
  }

  getSelectedVariant () {
    const selectedOptions = this.getSelectedOptions()
    return this.variants.find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )
  }

  async onChangeProductOption (event) {
    event.preventDefault()
    if (!this.isVariantForm) return

    const selectedVariant = this.getSelectedVariant()
    this.currentVariant = selectedVariant

    this.adjustPricing(selectedVariant)
    this.adjustStock(selectedVariant)
    this.adjustButtons(selectedVariant)
    this.updateVariantOptionStates()

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))
    }
  }

  updateVariantOptionStates () {
    if (!this.isVariantForm) return

    const selectedOptions = this.getSelectedOptions()
    const optionGroups = Array.from(this.form.querySelectorAll('.product-options > li'))

    optionGroups.forEach((group, optionIndex) => {
      const controls = Array.from(group.querySelectorAll('input[type="radio"], option'))

      controls.forEach((control) => {
        const candidateOptions = [...selectedOptions]
        candidateOptions[optionIndex] = control.value
        const candidateVariant = this.variants.find(variant =>
          JSON.stringify(variant.options) === JSON.stringify(candidateOptions)
        )
        const state = !candidateVariant
          ? 'unavailable'
          : candidateVariant.available
            ? 'available'
            : 'sold-out'

        control.dataset.variantState = state
        control.toggleAttribute('disabled', state !== 'available')
        control.setAttribute('aria-disabled', state !== 'available' ? 'true' : 'false')

        if (control.tagName === 'INPUT') {
          const label = this.form.querySelector(`label[for="${control.id}"]`)
          label?.setAttribute('data-variant-state', state)
        }
      })
    })
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.form.querySelector('.product-price-compare')
    const finalPrice = this.form.querySelector('.product-price-final')
    const compareBadge = this.form.querySelector('.product-sale-badge')
    const soldOutBadge = this.form.querySelector('.product-sold-out-badge')

    if (selectedVariant.price <= 0) {
      if (comparePrice) {
        comparePrice.textContent = ''
        comparePrice.setAttribute('hidden', 'hidden')
      }
      finalPrice.innerHTML = window.theme.locales.product.request_pricing
      finalPrice.classList.remove('product-price-final-sale')
      if (compareBadge) {
        compareBadge.textContent = ''
        compareBadge.setAttribute('hidden', 'hidden')
      }
    } else if (selectedVariant.compare_at_price > selectedVariant.price) {
      comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice.removeAttribute('hidden')
      finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
      finalPrice.classList.add('product-price-final-sale')
      compareBadge.removeAttribute('hidden')
      if (compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      comparePrice.textContent = ''
      comparePrice.setAttribute('hidden', 'hidden')
      finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
      finalPrice.classList.remove('product-price-final-sale')
      compareBadge.textContent = ''
      compareBadge.setAttribute('hidden', 'hidden')
    }

    if (selectedVariant.available) {
      soldOutBadge.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge.removeAttribute('hidden')
    }
  }

  adjustStock (selectedVariant) {
    if (!this.stockIndicator) return

    const update = () => {
      const threshold = Number(this.stockIndicator.dataset.lowStockThreshold || 0)
      const inventoryManaged = selectedVariant?.inventory_management
      const quantity = Number(selectedVariant?.inventory_quantity)
      let html = ''
      let state = 'unavailable'

      if (!selectedVariant) {
        html = `<p class="mb-0">${window.theme.locales.product.unavailable}</p>`
      } else if (!selectedVariant.available) {
        html = `<p class="mb-0">${window.theme.locales.product.stock_indicator?.no_stock || window.theme.locales.product.sold_out}</p>`
        state = 'out-of-stock'
      } else if (inventoryManaged && quantity > 0 && threshold > 0 && quantity <= threshold) {
        const lowStockLocale = window.theme.locales.product.stock_indicator?.low_stock_html
        const template = quantity === 1
          ? lowStockLocale?.one || window.theme.locales.product.stock_indicator?.normal_stock_html || 'Only, <b>{{ count }} item</b> is in stock!'
          : lowStockLocale?.other || window.theme.locales.product.stock_indicator?.normal_stock_html || 'Only, <b>{{ count }} items</b> are in stock!'
        html = `<p class="mb-2">${template.replace('{{ count }}', quantity)}</p>
          <div class="progress" role="progressbar" aria-valuemin="0" aria-valuemax="${threshold}" aria-valuenow="${quantity}" style="height: ${this.stockIndicator.dataset.progressHeight || 8}px;">
            <div class="progress-bar ${this.stockIndicator.dataset.lowStockBgColor || 'bg-primary'}" style="width: ${Math.min(quantity * 100 / threshold, 100)}%;"></div>
          </div>`
        state = 'low-stock'
      } else if (inventoryManaged && quantity > 0) {
        const template = window.theme.locales.product.stock_indicator?.normal_stock_html || 'Currently, this item has stock'
        html = `<p class="mb-0">${template.replace('{{ count }}', quantity)}</p>`
        state = 'in-stock'
      } else {
        html = `<p class="mb-0">${window.theme.locales.product.stock_indicator?.untracked_stock || 'Currently, this item has stock'}</p>`
        state = 'in-stock'
      }

      this.stockIndicator.innerHTML = html
      this.stockIndicator.dataset.stockState = state
      this.stockIndicator.classList.remove('is-updating')
    }

    this.stockIndicator.classList.add('is-updating')
    window.setTimeout(update, 90)
  }

  adjustButtons (selectedVariant) {
    if (!this.isVariantForm) {
      this.resetAtcButton()
      this.updateBuyButtonLink()
      return
    }

    this.atcBtn.classList.remove('is-success', 'is-error')
    this.atcBtn.removeAttribute('aria-invalid')

    if (selectedVariant) {
      if (selectedVariant.price <= 0) {
        if (this.atcBtn.tagName === 'A') {
          this.atcBtn.removeAttribute('disabled')
          this.atcBtn.removeAttribute('aria-disabled')
          this.atcBtn.innerHTML = window.theme.locales.product.business_quotation
        } else {
          this.atcBtn.setAttribute('disabled', 'disabled')
          this.atcBtn.setAttribute('aria-disabled', 'true')
          this.atcBtn.innerHTML = window.theme.locales.product.request_pricing
        }
        this.setBuyButtonDisabled(false)
      } else if (selectedVariant.available) {
        this.atcBtn.removeAttribute('disabled')
        this.setBuyButtonDisabled(false)
        this.atcBtn.setAttribute('aria-disabled', 'false')
        this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled')
        this.setBuyButtonDisabled(true)
        this.atcBtn.setAttribute('aria-disabled', 'true')
        this.atcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn.setAttribute('disabled', 'disabled')
      this.setBuyButtonDisabled(true)
      this.atcBtn.setAttribute('aria-disabled', 'true')
      this.atcBtn.innerHTML = window.theme.locales.product.unavailable
    }

    this.updateBuyButtonLink(selectedVariant)
  }

  async onSubmit (event) {
    event.preventDefault()

    if (this.isVariantForm && (!this.currentVariant || !this.currentVariant.available || this.currentVariant.price <= 0 || this.atcBtn.disabled)) return

    this.atcBtn.classList.add('loading')
    this.atcBtn.classList.remove('is-success', 'is-error')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    try {
      const formData = new FormData(this.form)

      const accessoryCheckboxes = this.getSelectedAccessories()
      this.setAccessoriesDisabled(true)

      let result

      if (accessoryCheckboxes.length > 0) {
        const items = []

        // Main product
        const mainId = this.form.querySelector('[name="id"]').value
        const mainQty = this.form.querySelector('[name="quantity"]')?.value || 1
        const mainItem = { id: mainId, quantity: parseInt(mainQty) }

        const propertyInputs = this.form.querySelectorAll('[name^="properties["]')
        if (propertyInputs.length > 0) {
          mainItem.properties = {}
          propertyInputs.forEach(input => {
            const name = input.name.match(/properties\[(.*?)\]/)[1]
            mainItem.properties[name] = input.value
          })
        }

        const sellingPlan = this.form.querySelector('[name="selling_plan"]')?.value
        if (sellingPlan) mainItem.selling_plan = sellingPlan

        items.push(mainItem)

        // Accessories
        accessoryCheckboxes.forEach(chk => {
          items.push({
            id: chk.value,
            quantity: 1
          })
        })

        result = await document.querySelector('cart-container').add({ items })
      } else {
        result = await document.querySelector('cart-container').add(formData)
      }

      this.atcBtn.classList.remove('loading')
      this.atcBtn.setAttribute('aria-busy', 'false')
      this.setAccessoriesDisabled(false)

      if (result?.ok === false) {
        this.atcBtn.classList.add('is-error')
        this.atcBtn.disabled = false
        this.atcBtn.setAttribute('aria-invalid', 'true')
        this.atcBtn.innerHTML = result.message || 'Error'
        window.setTimeout(() => this.adjustButtons(this.currentVariant), 1200)
        return
      }

      this.atcBtn.classList.add('is-success')
      this.atcBtn.innerHTML = 'Added'
      window.setTimeout(() => this.adjustButtons(this.currentVariant), 800)
    } catch (error) {
      this.atcBtn.classList.remove('loading')
      this.atcBtn.classList.add('is-error')
      this.atcBtn.disabled = false
      this.atcBtn.setAttribute('aria-busy', 'false')
      this.atcBtn.setAttribute('aria-invalid', 'true')
      this.atcBtn.innerHTML = 'Error'
      this.setAccessoriesDisabled(false)
      window.setTimeout(() => this.adjustButtons(this.currentVariant), 1200)
    }
  }

  onClickBuyBtn (event) {
    if (!this.currentVariant || !this.currentVariant.available || this.buyBtn?.getAttribute('aria-disabled') === 'true') {
      event?.preventDefault()
      return
    }

    this.buyBtn.classList.add('loading')
    this.updateBuyButtonLink(this.currentVariant)

    if (this.buyBtn.tagName === 'A') return

    const variantId = this.form.querySelector('[name="id"]').value
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)
    window.location.href = `/cart/${variantId}:${qty}`
  }

  updateBuyButtonLink (selectedVariant = this.currentVariant) {
    if (!this.buyBtn) return

    const variantId = selectedVariant?.id || this.form.querySelector('[name="id"]')?.value
    const qty = Number(this.form.querySelector('input[name="quantity"]')?.value || 1)

    if (!variantId || selectedVariant?.available === false) {
      this.buyBtn.removeAttribute('href')
      return
    }

    this.buyBtn.setAttribute('href', `/cart/${variantId}:${Math.max(qty, 1)}`)
  }

  setBuyButtonDisabled (disabled) {
    if (!this.buyBtn) return

    this.buyBtn.classList.toggle('is-disabled', disabled)
    this.buyBtn.setAttribute('aria-disabled', disabled ? 'true' : 'false')

    if (this.buyBtn.tagName === 'A') {
      if (disabled) {
        this.buyBtn.setAttribute('tabindex', '-1')
        this.buyBtn.removeAttribute('href')
      } else {
        this.buyBtn.removeAttribute('tabindex')
      }
      return
    }

    this.buyBtn.toggleAttribute('disabled', disabled)
  }

  resetAtcButton () {
    this.atcBtn.classList.remove('loading', 'is-success', 'is-error')
    this.atcBtn.disabled = this.atcInitiallyDisabled
    this.atcBtn.setAttribute('aria-busy', 'false')
    this.atcBtn.removeAttribute('aria-invalid')
    this.atcBtn.innerHTML = this.atcDefaultText
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)
    this.variantTransitionTimer = null
    this.variantTransitionToken = 0
    this.isUserInteracting = false
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    /** @type {Swiper} */
    this.thumbs = new window.Swiper(this.querySelector('.swiper-thumbs'), this.thumbsOptions())

    /** @type {Swiper} */
    this.main = new window.Swiper(this.querySelector('.swiper-main'), this.mainOptions())

    this.main.slideTo(initialSlide)

    this.setStickyPosition()
    this.setupGalleryInteractionState()
    this.syncGalleryAccessibility()
    this.listenVariantChange()
    this.initFullscreen()
  }

  initFullscreen () {
    this.fullscreenEnabled = this.dataset.fullscreen === 'true'
    if (!this.fullscreenEnabled) return

    this.fullscreenEl = this.querySelector('[data-gallery-fullscreen]')
    if (!this.fullscreenEl) return

    this.fullscreenSwiperEl = this.fullscreenEl.querySelector('[data-fullscreen-swiper]')
    this.fullscreenSlideCount = this.fullscreenSwiperEl.querySelectorAll('.swiper-wrapper > .swiper-slide').length
    this.counterEl = this.fullscreenEl.querySelector('[data-fullscreen-counter]')
    this.closeBtn = this.fullscreenEl.querySelector('[data-fullscreen-close]')
    this.prevBtn = this.fullscreenEl.querySelector('[data-fullscreen-prev]')
    this.nextBtn = this.fullscreenEl.querySelector('[data-fullscreen-next]')
    this.zoomBtn = this.querySelector('[data-gallery-zoom]')
    this.isFullscreenOpen = false
    this.pointerStartX = 0
    this.pointerStartY = 0

    this.zoomBtn?.addEventListener('click', (event) => {
      event.stopPropagation()
      this.openFullscreen(this.main.realIndex)
    })

    this.main.el.addEventListener('pointerdown', (event) => {
      this.pointerStartX = event.clientX
      this.pointerStartY = event.clientY
    })

    this.main.el.addEventListener('click', (event) => {
      const image = event.target.closest('.swiper-main .swiper-slide img')
      if (!image) return

      if (Math.hypot(event.clientX - this.pointerStartX, event.clientY - this.pointerStartY) > 8) return

      const slide = image.closest('.swiper-slide')
      const index = Array.from(slide.parentElement.children).indexOf(slide)
      if (index < 0) return
      this.openFullscreen(index)
    })

    const closeFullscreen = (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.closeFullscreen()
    }
    this.closeBtn.addEventListener('click', closeFullscreen)
    this.closeBtn.addEventListener('pointerup', closeFullscreen)
    this.closeBtn.addEventListener('touchend', closeFullscreen, { passive: false })
    this.prevBtn.addEventListener('click', () => this.fullscreenSwiper?.slidePrev())
    this.nextBtn.addEventListener('click', () => this.fullscreenSwiper?.slideNext())

    this.boundFullscreenKeydown = (event) => this.onFullscreenKeydown(event)
  }

  onFullscreenKeydown (event) {
    if (event.key === 'Escape') {
      this.closeFullscreen()
      return
    }
    if (event.key === 'ArrowLeft') {
      this.fullscreenSwiper?.slidePrev()
      return
    }
    if (event.key === 'ArrowRight') {
      this.fullscreenSwiper?.slideNext()
    }
  }

  openFullscreen (index) {
    if (this.isFullscreenOpen) return

    const total = this.fullscreenSlideCount
    if (total === 0) return

    this.isFullscreenOpen = true
    this.lastFocused = this.zoomBtn
    this.classList.add('is-fullscreen-open')
    this.fullscreenEl.hidden = false
    document.body.classList.add('gallery-fullscreen-open')
    this.pauseMainMedia()

    if (!this.fullscreenSwiper) {
      this.fullscreenSwiper = new window.Swiper(this.fullscreenSwiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: this.reducedMotion ? 0 : 250,
        loop: total > 1,
        on: {
          slideChange: () => this.syncFullscreenCounter()
        }
      })
    }

    this.fullscreenSwiper.update()
    if (this.fullscreenSwiper.params.loop) {
      this.fullscreenSwiper.slideToLoop(Math.min(Math.max(index, 0), total - 1), 0)
    } else {
      this.fullscreenSwiper.slideTo(Math.min(Math.max(index, 0), total - 1), 0)
    }

    this.syncFullscreenCounter()
    document.addEventListener('keydown', this.boundFullscreenKeydown, false)
    this.closeBtn.focus()
  }

  syncFullscreenCounter () {
    if (!this.counterEl || !this.fullscreenSwiper) return
    const total = this.fullscreenSlideCount
    const current = (this.fullscreenSwiper.realIndex % total) + 1
    this.counterEl.textContent = `${current} / ${total}`
  }

  closeFullscreen () {
    if (!this.isFullscreenOpen) return

    this.pauseFullscreenMedia()

    const targetIndex = this.fullscreenSwiper ? this.fullscreenSwiper.realIndex : null
    if (targetIndex !== null && this.main) {
      this.main.slideTo(targetIndex)
    }

    this.fullscreenEl.hidden = true
    this.classList.remove('is-fullscreen-open')
    document.body.classList.remove('gallery-fullscreen-open')
    document.removeEventListener('keydown', this.boundFullscreenKeydown, false)
    this.isFullscreenOpen = false
    if (this.lastFocused) {
      this.lastFocused.focus()
    }
  }

  pauseMainMedia () {
    this.querySelectorAll('.swiper-main video').forEach((video) => {
      if (!video.paused) video.pause()
    })
  }

  pauseFullscreenMedia () {
    this.fullscreenSwiperEl.querySelectorAll('video').forEach((video) => {
      if (!video.paused) video.pause()
    })
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView),
      spaceBetween: 10
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: 'bullets',
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      }
    }
    return options
  }

  setupGalleryInteractionState () {
    this.main.on('touchStart', () => {
      this.isUserInteracting = true
    })

    this.main.on('touchEnd', () => {
      this.isUserInteracting = false
    })

    this.main.on('slideChange', () => {
      this.syncGalleryAccessibility()
    })

    this.main.on('transitionEnd', () => {
      this.syncGalleryAccessibility()
    })

    this.querySelectorAll('.swiper-thumbs .swiper-slide').forEach((slide, index) => {
      slide.setAttribute('role', 'button')
      slide.setAttribute('tabindex', '0')
      slide.setAttribute('aria-label', `View product media ${index + 1}`)
      slide.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        this.main.slideTo(index)
      })
    })

    this.querySelectorAll('.swiper-button-prev, .swiper-button-next').forEach((button) => {
      button.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        button.click()
      })
    })

    this.syncNavigationAccessibility()
  }

  syncGalleryAccessibility () {
    this.querySelectorAll('.swiper-thumbs .swiper-slide').forEach((slide, index) => {
      const isActive = index === this.main.realIndex
      slide.setAttribute('aria-current', isActive ? 'true' : 'false')
    })

    this.syncNavigationAccessibility()
  }

  syncNavigationAccessibility () {
    this.querySelectorAll('.swiper-button-prev, .swiper-button-next').forEach((button) => {
      const isDisabled = button.classList.contains('swiper-button-disabled')
      button.setAttribute('role', 'button')
      button.setAttribute('tabindex', isDisabled ? '-1' : '0')
      button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false')
    })
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      if (selectedVariant.featured_media) {
        this.slideToVariantMedia(selectedVariant.featured_media.position - 1)
      }
    }, false)
  }

  slideToVariantMedia (index) {
    window.clearTimeout(this.variantTransitionTimer)
    this.variantTransitionToken += 1

    if (this.reducedMotion || this.isUserInteracting) {
      this.main.slideTo(index)
      this.syncGalleryAccessibility()
      return
    }

    const token = this.variantTransitionToken
    this.classList.add('is-variant-switching')

    this.variantTransitionTimer = window.setTimeout(() => {
      if (token !== this.variantTransitionToken) return
      this.main.slideTo(index)
      window.setTimeout(() => {
        if (token !== this.variantTransitionToken) return
        this.classList.remove('is-variant-switching')
        this.syncGalleryAccessibility()
      }, 180)
    }, 70)
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)
